import { HeartPulse, GraduationCap, Briefcase } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { SectionTitle, Reveal } from "./Decor";

const tints = ["bg-sky-50 ring-sky-100", "bg-pink-50 ring-pink-100", "bg-yellow-50 ring-yellow-100", "bg-purple-50 ring-purple-100"];

export const AboutSection = ({ c, s }) => {
  const p = c?.profile || {};
  const st = c?.settings || {};
  const quals = (c?.qualifications || []).filter((q) => q.title);
  const exp = (c?.experience || []).filter((x) => x.role || x.place);
  const specs = (p.specializations || []).filter(Boolean);

  return (
    <section id="about" className="relative bg-white py-16 sm:py-20" data-testid="about-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle id="about" title={s.title} subtitle={s.subtitle} />
        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <Reveal className="rounded-[2rem] bg-sky-50 p-6 ring-1 ring-sky-100 sm:p-8">
            <p data-testid="about-bio" className="whitespace-pre-line text-base leading-relaxed text-slate-700">{p.bio}</p>
            {p.extra_info && <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{p.extra_info}</p>}
            {specs.length > 0 && (
              <div className="mt-6">
                <p className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                  <HeartPulse className="h-4 w-4 text-pink-500" /> {st.specialization_label || "Specialization"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="about-specializations">
                  {specs.map((x, i) => (
                    <span key={i} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-blue-900 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">{x}</span>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
          {st.about_decor_image && (
            <Reveal delay={0.15} className="mx-auto w-full max-w-xs self-end lg:max-w-sm">
              <img src={imgUrl(st.about_decor_image)} alt="" aria-hidden="true" className="w-full" />
            </Reveal>
          )}
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {quals.length > 0 && (
            <div>
              <h3 className="font-display flex items-center gap-2 text-2xl font-semibold text-blue-900">
                <GraduationCap className="h-6 w-6 text-orange-500" /> {st.qualifications_label || "Qualifications"}
              </h3>
              <div className="mt-5 grid gap-3" data-testid="about-qualifications">
                {quals.map((q, i) => (
                  <Reveal key={q.id || i} delay={i * 0.06} className={`rounded-3xl p-5 ring-1 ${tints[i % 4]}`}>
                    <p className="font-display text-lg font-semibold text-blue-900">{q.title}</p>
                    {(q.institution || q.year) && <p className="mt-1 text-sm text-slate-600">{[q.institution, q.year].filter(Boolean).join(" · ")}</p>}
                  </Reveal>
                ))}
              </div>
            </div>
          )}
          {exp.length > 0 && (
            <div>
              <h3 className="font-display flex items-center gap-2 text-2xl font-semibold text-blue-900">
                <Briefcase className="h-6 w-6 text-pink-500" /> {st.experience_label || "Professional Experience"}
              </h3>
              <ol className="mt-5 space-y-4 border-l-2 border-dashed border-sky-200 pl-6" data-testid="about-experience">
                {exp.map((x, i) => (
                  <Reveal as="li" key={x.id || i} delay={i * 0.06} className="relative">
                    <span className="absolute -left-[33px] top-1.5 h-4 w-4 rounded-full border-4 border-white bg-pink-400 shadow" />
                    <p className="font-display text-lg font-semibold text-blue-900">{x.role}</p>
                    <p className="text-sm font-medium text-slate-700">{x.place}</p>
                    {x.period && <p className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-orange-500">{x.period}</p>}
                    {x.description && <p className="mt-1 text-sm text-slate-600">{x.description}</p>}
                  </Reveal>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
